import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { Star, Heart, ShoppingCart, Minus, Plus, Truck, Shield, RefreshCw, ChevronLeft, ChevronRight, Check, X, ShieldCheck, MessageSquare, Gift, ArrowRight, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { GiftCardSuccessModal } from '../components/GiftCardSuccessModal';
import { GiftCardPaymentModal } from '../components/GiftCardPaymentModal';
import { GiftCardInfo, sanitizeCatalogueHtml } from '../components/GiftCardInfo';
import { FeatureDetailModal } from '../components/FeatureDetailModal';
import { PincodeCheck } from '../components/PincodeCheck';
import { ProductCard } from '../components/ProductCard';
import { GiftCardTile } from '../components/GiftCardTile';
import { GiftCardGatewayModal } from '../components/GiftCardGatewayModal';
import { GIFT_CARD_MAX_QUANTITY } from '../lib/rewardCatalogue';
import { getRewardDenominations, getRewardRange, getRewardPricing, formatDiscountRate } from '../lib/rewardDiscount';
import { useFaceQuote } from '../lib/giftCardQuote';
import { fetchSettlementVerdict, fetchRefund } from '../lib/paymentStatus';
import { useCart } from '../context/CartContext';
import { useGiftCardCart } from '../context/GiftCardCartContext';
import { useWishlist } from '../context/WishlistContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { CollapsibleDescription } from '../components/CollapsibleDescription';
import { getRewardImageCandidates, getRewardName } from '../lib/rewardImage';
import { PLACEHOLDER_IMAGE } from '../lib/placeholder';
import { formatINR, formatMoney } from '../lib/money';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

// The per-line provider cap lives with the catalogue helpers now — the cart
// stepper enforces the same number and the two must not drift.

/**
 * Turns a failed /giftcards/purchase into something a customer can act on.
 *
 * The backend's `detail` is written for us, not for them, so it is kept only
 * when nothing more specific applies. Two cases matter most:
 *
 *   503 — our Razorpay distributor float cannot cover this card's value. It is
 *         an ops problem, not the customer's, and it is fixed by picking a
 *         cheaper card or waiting for a top-up. Saying "out of stock" would be
 *         a lie about the brand.
 *   400 — several distinct causes share this code, so the provider's own text
 *         is the most useful thing available; we only reframe the ones whose
 *         wording is unusable.
 */
function purchaseErrorMessage(status, detail) {
  const text = String(detail || '');

  if (status === 503) {
    return 'This gift card is temporarily unavailable. Please try another card, or check back shortly.';
  }

  if (status === 400) {
    if (/quantit|max|limit/i.test(text)) {
      return `You can buy up to ${GIFT_CARD_MAX_QUANTITY} of the same gift card in one order.`;
    }
    if (/price|amount|mismatch/i.test(text)) {
      return 'The price changed while you were on this page. Please reload and try again.';
    }
    if (/denomination|issuab/i.test(text)) {
      return 'That amount is no longer available for this card. Please pick another denomination.';
    }
    if (/inactive|expired|not yet|unavailable/i.test(text)) {
      return 'This gift card is no longer available for purchase.';
    }
    if (/cod|cash on delivery/i.test(text)) {
      return 'Gift cards cannot be paid for on delivery. Please pay online or from your wallet.';
    }
  }

  return text || 'Failed to initiate purchase';
}

export default function ProductPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const paymentRef = searchParams.get('ref');
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { addGiftCard } = useGiftCardCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedDenomination, setSelectedDenomination] = useState(null);
  // Range cards: the face value the customer typed, and the server's answer for
  // it. The input holds face; `denomination` stays payable everywhere it is sent.
  const [rangeFace, setRangeFace] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [voucherDetails, setVoucherDetails] = useState(null);
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const [paymentModal, setPaymentModal] = useState({ open: false, url: '', orderId: '', status: 'pending' });
  const [giftPayModal, setGiftPayModal] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const pollRef = useRef(null);
  const statusTimeoutRef = useRef(null);
  const statusAttemptsRef = useRef(0);

  const inWishlist = product ? isInWishlist(product.product_id) : false;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      /*
        Wipe the previous card's pricing before fetching, not after.

        These survive a productId change and a refresh otherwise, so the panel
        could show the last card's payable, or a figure priced under a discount
        an admin had since switched off. Pricing is never carried across a load —
        it is rebuilt from the response every time.
      */
      setProduct(null);
      setSelectedDenomination(null);
      setRangeFace('');
      try {
        if (productId.startsWith('reward_')) {
          const rewardId = productId.replace('reward_', '');
          let rewardFromState = location.state?.reward;

          /*
            No router state means a direct link, a bookmark, a shared URL or a
            plain refresh. This used to fall through to /api/products/reward_XXXX
            — which is not a product — and every one of those cases showed
            "Product Not Found". The by-id lookup shipped on 2026-08-07, so the
            page can now build itself from the URL alone.
          */
          if (!rewardFromState) {
            const res = await fetch(`${API}/giftcards/rewards/${rewardId}`, { credentials: 'include' });
            if (res.ok) rewardFromState = await res.json();
          }

          if (rewardFromState) {
            // Two denomination shapes (values in paise):
            //  - fixed: eligible_fixed_denomination = [v1, v2, ...] -> chips
            //  - range: eligible_fixed_denomination = [] and
            //           eligible_range_denomination = [min, max]     -> amount input
            // Prefer the admin-marked-up display_* values (face + margin) when the
            // backend sends them so the customer sees/pays the selling price.
            // The public catalogue exposes ONLY display (marked-up) values. The
            // `eligible_*` face denominations and the `markup` config were removed
            // from this endpoint deliberately — they are our buying rates.
            //
            // So the frontend works entirely in display values and sends those as
            // `denomination`. The backend reverses the markup and validates the
            // result against the provider's eligible list, which is the only place
            // that list exists. Do not "improve" this by sending a face value: it
            // cannot be computed from what this endpoint returns.
            /*
              Paired {price, face, savings} from the catalogue, sorted. This used
              to read `display_fixed_denomination` and pair it with a face value
              derived from the price, which was wrong on every clamped card — the
              server lowers a discount that would sell below cost, and division
              cannot undo the ceil it rounds with. Nothing is computed here now.
            */
            const options = getRewardDenominations(rewardFromState);
            const range = getRewardRange(rewardFromState);

            const denominationType =
              rewardFromState.denomination_type || (options.length ? 'fixed' : 'range');

            const fixedOptions = options.map((o, idx) => ({
              id: `${rewardId}_${idx}`,
              value: o.price,
              face: o.face,
              savings: o.savings,
            }));

            const startingPrice = rewardFromState.display_price != null
              ? rewardFromState.display_price / 100
              : (options[0]?.price ?? range?.minPayable ?? 0);

            // Face values: the input holds what the card is worth, not what it
            // costs. `denomination` stays payable everywhere it is sent.
            const minAmount = range?.minFace ?? startingPrice;
            const maxAmount = range?.maxFace ?? startingPrice;

            setProduct({
              product_id: productId,
              name: getRewardName(rewardFromState) || rewardFromState.name,
              description: rewardFromState.display_parameters?.description || rewardFromState.description,
              brand: 'Gift Card',
              // Every candidate, in reliability order — not `a || b`. The first
              // field is a presigned link that always 403s, so picking it and
              // discarding the working brand logo left the gallery empty.
              images: getRewardImageCandidates(rewardFromState),
              price: startingPrice,
              stock: 999,
              // The catalogue ships redemption instructions, terms, validity and
              // channels for every reward (100/100 measured) and the page was
              // dropping all of it — leaving customers with no idea how or where
              // to actually use the voucher they just paid for.
              redemption_instructions: rewardFromState.display_parameters?.redemption_instructions,
              terms: rewardFromState.display_parameters?.terms,
              redemption_channels: rewardFromState.display_parameters?.redemption_channels,
              redemption_url: rewardFromState.display_parameters?.redemption_url,
              valid_till: rewardFromState.end_date,
              brand_website: rewardFromState.brand?.website,
              brand_name: rewardFromState.brand?.name,
              denomination_type: denominationType,
              range: range || null,
              min_amount: minAmount,
              max_amount: maxAmount,
              /*
                The picker's options. This key was being set twice in this same
                literal — once here, and again below with the catalogue's raw
                `denominations`. The second won, so the moment the backend
                stopped publishing that field every fixed card lost its chips
                and rendered as a single-amount card. The raw fields are not
                needed here: `fixedOptions` already carries face and savings per
                option, read from the catalogue by getRewardDenominations.
              */
              denominations: denominationType === 'fixed' ? fixedOptions : [],
              // Card-level pricing, read never recomputed. Rupees — the legacy
              // display_*/face_price pair on the same object is paise.
              face_value: rewardFromState.face_value,
              discount_amount: rewardFromState.discount_amount,
              payable_amount: rewardFromState.payable_amount,
              discount_label: rewardFromState.discount_label,
              face_price: rewardFromState.face_price,
              savings: rewardFromState.savings,
              display_price: rewardFromState.display_price,
              is_gift_card: true
            });

            // Arriving from the cart, the amount and count are already decided —
            // re-picking the cheapest option here would quietly change what the
            // customer thought they were paying for.
            const preset = Number(location.state?.denomination);
            const presetChip = Number.isFinite(preset) && preset > 0
              ? (fixedOptions.find((o) => o.value === preset) || { id: `${rewardId}_preset`, value: preset })
              : null;

            /*
              Nothing is pre-selected on a fixed card. It used to default to the
              cheapest, which meant the headline, the buttons and the cart all
              carried a value the customer never chose — and on a nine-amount
              card the one they were least likely to want. Buying is blocked
              until a card is picked.

              A range card has no list to pick from, so its floor stands as the
              starting value and the input takes it from there.
            */
            setSelectedDenomination(
              presetChip
              || (denominationType === 'range'
                ? { id: `${rewardId}_range`, value: range?.minPayable ?? minAmount, face: minAmount }
                : null)
            );
            if (denominationType === 'range') setRangeFace(minAmount);
            if (presetChip) setProduct((prev) => ({ ...prev, price: presetChip.value }));

            const presetQty = Number(location.state?.quantity);
            if (Number.isFinite(presetQty) && presetQty >= 1) {
              setQuantity(Math.min(GIFT_CARD_MAX_QUANTITY, Math.round(presetQty)));
            }

            setLoading(false);
            return;
          }
        }

        // Parallelize Product and Review fetching
        const [productRes, reviewsRes] = await Promise.all([
          fetch(`${API}/products/${productId}`),
          fetch(`${API}/reviews/${productId}`)
        ]);

        if (productRes.ok) {
          const productData = await productRes.json();
          setProduct(productData);
          
          // Related products depend on category_id
          const relatedRes = await fetch(`${API}/products?category=${productData.category_id}&limit=4`);
          if (relatedRes.ok) {
            const relatedData = await relatedRes.json();
            setRelatedProducts((relatedData.products || []).filter(p => p.product_id !== productId));
          }
        }

        if (reviewsRes.ok) {
          const reviewsData = await reviewsRes.json();
          setReviews(reviewsData);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Product not found');
      } finally {
        setLoading(false);
      }
    };

    // Reset before the fetch, not after. The gift-card branch of fetchData has
    // no await ahead of it, so it completes synchronously — a reset placed after
    // the call used to run *later* than the values it was resetting, which is
    // what would wipe a quantity handed over from the cart.
    setQuantity(1);
    setSelectedImage(0);
    fetchData();
  }, [productId, location.state]);

  useEffect(() => {
    if (paymentRef && product?.is_gift_card) {
      checkPaymentStatus(paymentRef);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentRef, product]);

  // Wallet balance, so the gift-card payment chooser can show a real number.
  // Only gift cards need it here — physical products pay through CheckoutPage,
  // which reads the balance itself.
  const refreshWalletBalance = useCallback(async () => {
    if (!user) return 0;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/wallet`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (!res.ok) return 0;
      const data = await res.json();
      const balance = Number(data.balance) || 0;
      setWalletBalance(balance);
      return balance;
    } catch (error) {
      console.error('Could not read wallet balance:', error);
      return 0;
    }
  }, [user]);

  useEffect(() => {
    if (!user || !product?.is_gift_card) return;
    refreshWalletBalance();
  }, [user, product?.is_gift_card, refreshWalletBalance]);

  const checkPaymentStatus = async (ref) => {
    try {
      setIsProcessingOrder(true);
      const response = await fetch(`${API}/payment/status/${ref}`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to check payment status');

      const data = await response.json();

      if (data.status === 'success') {
        const order_id = data.data?.transactionId || data.transaction_id;
        if (order_id) {
          fetchVoucherDetails(order_id);
        } else {
          toast.success('Payment successful! Processing your gift card...');
          // Optional: Poll again or show info
        }
      } else if (data.status === 'failed') {
        toast.error('Payment failed. Please try again or contact support.');
        setIsProcessingOrder(false);
      } else {
        // Still pending — retry, but cap attempts and track the timeout so it can
        // be cleared on unmount (otherwise it polls forever + updates unmounted state).
        statusAttemptsRef.current += 1;
        if (statusAttemptsRef.current >= 40) { // ~2 min
          toast.info('Still confirming your payment. Check "My Orders" for the latest status.');
          setIsProcessingOrder(false);
          return;
        }
        statusTimeoutRef.current = setTimeout(() => checkPaymentStatus(ref), 3000);
      }
    } catch (err) {
      console.error('Status check error:', err);
      setIsProcessingOrder(false);
    }
  };

  useEffect(() => {
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current);
      statusTimeoutRef.current = null;
    }
  };

  const startPollingPaymentStatus = ({ orderId, referenceId }) => {
    stopPolling();

    const check = async () => {
      const verdict = await fetchSettlementVerdict({ referenceId, orderId });

      if (verdict === 'paid') {
        stopPolling();
        setPaymentModal(prev => ({ ...prev, status: 'success' }));
        setTimeout(() => {
          setPaymentModal({ open: false, url: '', orderId: '', status: 'pending' });
          fetchVoucherDetails(orderId);
        }, 2000);
      } else if (verdict === 'failed') {
        stopPolling();
        // A failed order may still have taken and returned the money.
        const refund = await fetchRefund(orderId);
        setPaymentModal(prev => ({ ...prev, status: 'failed', refund }));
      }
      // 'pending' and 'unknown' both keep polling — a dropped request is not a
      // verdict, and treating it as one would strand a paid customer.
    };

    // Every 3s, and once immediately: the old 5s interval also waited a full
    // cycle before its first look, so a fast UPI payment sat unacknowledged for
    // up to ten seconds while the customer stared at the gateway.
    check();
    pollRef.current = setInterval(check, 3000);
  };

  const manualCheckStatus = async () => {
    if (checkingStatus) return;
    setCheckingStatus(true);

    const verdict = await fetchSettlementVerdict({
      referenceId: paymentModal.referenceId,
      orderId: paymentModal.orderId,
    });

    if (verdict === 'paid') {
      stopPolling();
      setPaymentModal(prev => ({ ...prev, status: 'success' }));
      setTimeout(() => {
        setPaymentModal({ open: false, url: '', orderId: '', status: 'pending' });
        fetchVoucherDetails(paymentModal.orderId);
      }, 2000);
    } else if (verdict === 'failed') {
      stopPolling();
      const refund = await fetchRefund(paymentModal.orderId);
      setPaymentModal(prev => ({ ...prev, status: 'failed', refund }));
    } else if (verdict === 'unknown') {
      toast.error('Could not reach the server. Your payment is safe — the check will retry on its own.');
    } else {
      /*
        This used to also fire checkPaymentStatus(order_id) against
        /api/payment/status/{reference_id}. Those are different id spaces: the
        endpoint answers 404 "Payment record not found" for a GC… order id, so
        the call could only ever fail — and on the way it flipped
        isProcessingOrder and started a second, competing poll loop.
      */
      toast.info('Not confirmed yet — this can take a few seconds. We are still checking.');
    }

    setCheckingStatus(false);
  };


  /**
   * Shows the voucher code and PIN after a successful gift-card payment.
   *
   * Two different ids are in play and they are not interchangeable:
   *   order_id    — ours, "GC…". Resolves on /api/orders/{id}.
   *   voucher_ref — the provider's order id. Only this one resolves on
   *                 /api/giftcards/razorpay/orders/{id}, which is where the
   *                 code and PIN actually live.
   *
   * The purchase response carries `voucher_ref`; for the gateway path we may only
   * have the order, so the order is read first to recover it.
   */
  const fetchVoucherDetails = async (orderId, voucherRef) => {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const readVoucher = (payload) => {
      const items = payload?.order?.order_items || payload?.items || [];
      return (
        payload?.vouchers?.[0] ||
        items[0]?.vouchers?.[0] ||
        payload?.order?.vouchers?.[0]
      );
    };

    try {
      let ref = voucherRef;

      // No ref in hand (gateway path) — recover it from the order.
      if (!ref) {
        const orderRes = await fetch(`${API}/orders/${orderId}`, { headers, credentials: 'include' });
        if (orderRes.ok) {
          const orderData = await orderRes.json();
          const inline = readVoucher(orderData);
          if (inline) {
            setVoucherDetails(inline);
            setShowSuccessModal(true);
            return;
          }
          ref =
            orderData.voucher_ref ||
            orderData.order?.voucher_ref ||
            (orderData.items || orderData.order?.order_items || [])[0]?.voucher_ref;
        }
      }

      if (ref) {
        const res = await fetch(`${API}/giftcards/razorpay/orders/${ref}`, { headers, credentials: 'include' });
        if (res.ok) {
          const voucher = readVoucher(await res.json());
          if (voucher) {
            setVoucherDetails(voucher);
            setShowSuccessModal(true);
            return;
          }
        }
      }

      // Paid and confirmed, but the code isn't readable yet. That is not a
      // failure — say so and hand them to the order rather than implying one.
      toast.success('Payment confirmed — your gift card is in My Orders.');
      navigate(`/order-success/${orderId}`);
    } catch (err) {
      console.error('Voucher fetch error:', err);
      toast.success('Payment confirmed — check My Orders for your gift card.');
      navigate('/dashboard/orders');
    } finally {
      setIsProcessingOrder(false);
    }
  };

  const handleAddToCart = async () => {
    try {
      await addToCart(product.product_id, quantity);
      toast.success('Added to cart');
    } catch (error) {
      toast.error(error.message);
    }
  };

  /**
   * Gift cards go to their own basket, not `/api/cart/add` — that endpoint takes
   * a product_id and has nowhere to put the chosen denomination. See
   * GiftCardCartContext for why the two baskets are separate.
   */
  const handleAddGiftCardToCart = () => {
    const amount = Number(selectedDenomination?.value ?? product.price);

    if (product.denomination_type === 'range') {
      const faceAmt = Number(selectedDenomination?.face ?? rangeFace);
      if (!Number.isFinite(faceAmt) || faceAmt < product.min_amount || faceAmt > product.max_amount) {
        toast.error(`Amount must be between ${formatPrice(product.min_amount)} and ${formatPrice(product.max_amount)}`);
        return;
      }
    }

    try {
      // Prefer the raw catalogue reward we were routed with — the cart snapshots
      // it so it can route the customer back here, and there is no by-id reward
      // endpoint to rebuild it from.
      const source = location.state?.reward || {
        reward_id: product.product_id,
        name: product.name,
        display_image_candidates: product.images || [],
        markup_type: product.markup_type,
        markup_value: product.markup_value,
      };

      const { added, capped } = addGiftCard(source, amount, quantity);

      if (added === 0) {
        toast.error(`Your cart already holds the maximum ${GIFT_CARD_MAX_QUANTITY} of this card at ${formatPrice(amount)}.`);
        return;
      }
      if (capped) {
        toast.warning(`Added ${added} — ${GIFT_CARD_MAX_QUANTITY} is the most of one card at one amount that can be issued in a single order.`);
        return;
      }
      toast.success(`${added} × ${formatPrice(amount)} ${product.name} added to cart`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleBuyNow = async () => {
    if (!user) {
      // Store pending checkout in localStorage
      localStorage.setItem('pendingCheckout', JSON.stringify({
        productId: product.product_id,
        quantity: quantity,
        isGiftCard: !!product.is_gift_card,
        denomination: selectedDenomination?.value || product.price
      }));
      toast.info('Please login to continue checkout');
      navigate('/login?redirect=/checkout');
      return;
    }

    if (product.is_gift_card) {
      // Range cards take a variable amount — enforce the min/max before we pay.
      if (product.denomination_type === 'range') {
        const faceAmt = Number(selectedDenomination?.face ?? rangeFace);
        if (!faceAmt || Number.isNaN(faceAmt) || faceAmt < product.min_amount || faceAmt > product.max_amount) {
          toast.error(`Amount must be between ${formatPrice(product.min_amount)} and ${formatPrice(product.max_amount)}`);
          return;
        }
      }

      // How to pay is decided in the chooser; it calls submitGiftCardPurchase.
      setGiftPayModal(true);
      return;
    }

    try {
      await addToCart(product.product_id, quantity);
      navigate('/cart');
    } catch (error) {
      toast.error(error.message);
    }
  };

  /**
   * Places the gift-card order once a payment method has been picked.
   *
   * Two settlement shapes come back and they are not interchangeable:
   *   wallet in full — backend confirms outright, vouchers are ready now
   *   online         — a payment_url to open in the modal, then poll for status
   *
   * There is no COD path: a gift card is issued digitally the instant payment
   * clears, so there is no delivery at which cash could be collected.
   */
  const submitGiftCardPurchase = async ({ paymentMethod, walletAmount, total }) => {
    // Defence in depth. The modal already caps wallet spend at the displayed
    // balance, but that balance can be stale — spent in another tab, or never
    // fetched because /api/wallet failed. Re-read it before committing money.
    // This protects the UX only: the backend must reject an over-spend as well,
    // since anyone can post to /giftcards/purchase directly.
    if (walletAmount > 0) {
      const fresh = await refreshWalletBalance();
      if (walletAmount > fresh) {
        toast.error(`Wallet has only ${formatPrice(fresh)}. Please try again.`);
        return;
      }
      if (paymentMethod === 'wallet' && fresh < total) {
        toast.error(`Wallet balance ${formatPrice(fresh)} does not cover ${formatPrice(total)}.`);
        return;
      }
    }

    try {
      setIsProcessingOrder(true);
      const rewardId = product.product_id.replace('reward_', '');
      const amount = selectedDenomination?.value || product.price;

      // Display (marked-up) paise. The backend reverses the markup and checks the
      // result against the provider's eligible list — the face value is not
      // derivable here, because the public catalogue no longer exposes it.
      const denominationPaise = Math.round(amount * 100);

      const token = localStorage.getItem('token');
      const buyResponse = await fetch(`${API}/giftcards/purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({
          reward_id: rewardId,
          denomination: denominationPaise,
          quantity: quantity,
          amount: total ?? amount * quantity,
          payment_method: paymentMethod,
          ...(walletAmount > 0 ? { wallet_amount: walletAmount } : {})
        })
      });

      // A 409 is a duplicate of a purchase made within the last 90 seconds. It
      // is not a failure — the first request is in flight — and retrying it is
      // how a customer ends up paying twice. Fall through to the normal flow.
      if (buyResponse.status === 409) {
        toast.info('This purchase is already being processed. Check My Orders in a moment.');
        setIsProcessingOrder(false);
        setGiftPayModal(false);
        return;
      }

      if (!buyResponse.ok) {
        const errorData = await buyResponse.json().catch(() => ({}));
        throw new Error(purchaseErrorMessage(buyResponse.status, errorData.detail));
      }

      const buyData = await buyResponse.json();
      // order_id first: "GC…" is ours and resolves on /api/orders. transaction_id
      // is only a fallback for the older response shape.
      const orderId = buyData.order_id || buyData.transaction_id || buyData.data?.orderId || buyData.data?.transactionId;
      const paymentUrl = buyData.payment_url || buyData.data?.paymentUrl;
      // The gateway's own handle on this payment. /api/payment/status answers on
      // this and nothing else, and it settles a step before the order does.
      const referenceId = buyData.reference_id || buyData.data?.referenceId;
      // The provider's order id — the only one that resolves on the Razorpay
      // passthrough, which is where the voucher code and PIN live.
      const voucherRef = buyData.voucher_ref || buyData.data?.voucher_ref;

      if (paymentMethod === 'wallet' && !paymentUrl) {
        // Do NOT announce a wallet payment just because no gateway URL came back.
        // The absence of a payment_url only means "no gateway leg"; it is not
        // evidence that money moved. Confirm from the response before saying so.
        // Confirmed live: the wallet purchase returns wallet_debited:true,
        // payment_status:"paid" and status:"confirmed" together. Any one of them
        // is enough; requiring all three would be brittle.
        const settled =
          buyData.wallet_debited === true ||
          String(buyData.payment_status || '').toLowerCase() === 'paid' ||
          ['confirmed', 'success', 'completed', 'paid'].includes(
            String(buyData.status || '').toLowerCase()
          );

        setGiftPayModal(false);
        refreshWalletBalance();

        if (!settled) {
          setIsProcessingOrder(false);
          toast.error(
            'Wallet payment is not supported for gift cards yet — your order was created but nothing has been charged. Please pay online instead.',
            { duration: 9000 }
          );
          return;
        }

        toast.success(`Paid ${formatPrice(walletAmount)} from your wallet`);
        if (orderId) {
          fetchVoucherDetails(orderId, voucherRef);
        } else {
          setIsProcessingOrder(false);
        }
        return;
      }

      if (paymentUrl) {
        setGiftPayModal(false);
        // Opened from inside the click that started checkout, so the popup
        // blocker lets it through. If it is blocked anyway, `opened` is null and
        // the modal shows a button for the customer to open it themselves.
        setPaymentModal({
          open: true,
          url: paymentUrl,
          orderId,
          referenceId,
          amount: total ?? amount * quantity,
          status: 'pending',
        });
        startPollingPaymentStatus({ orderId, referenceId });
        return;
      }

      throw new Error(buyData.message || 'Payment could not be initialized. Please try again.');
    } catch (err) {
      console.error('Purchase error:', err);
      toast.error(err.message);
      setIsProcessingOrder(false);
    }
  };

  const handleWishlist = async () => {
    if (!user) {
      toast.error('Please login to add to wishlist');
      return;
    }
    try {
      if (inWishlist) {
        await removeFromWishlist(product.product_id);
        toast.success('Removed from wishlist');
      } else {
        await addToWishlist(product.product_id);
        toast.success('Added to wishlist');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const formatPrice = formatINR;

  // Gift-card pricing. The selling price already has the discount applied; the
  // face value is the other side of the server's own equation, so it can be
  // shown struck through rather than guessed at. Both null without a discount.
  /*
    The header's figures, which describe the card and not the choice: the
    cheapest value on offer and the discount that applies to it.
  */
  const cardPricing = product?.is_gift_card ? getRewardPricing(product) : null;
  const cardStartingValue = product?.is_gift_card
    ? (product.denominations?.[0]?.face ?? product.denominations?.[0]?.value
      ?? cardPricing?.face ?? cardPricing?.price ?? product.price)
    : null;
  const giftCardLabel = cardPricing?.label || null;

  /*
    A typed face value, priced by the server. Same hook the listing's quick-add
    uses — one implementation, so the two screens cannot quote differently.
  */
  const { row: rangeQuote, pricing: rangePricing } = useFaceQuote({
    rewardId: product?.is_gift_card && product.denomination_type === 'range'
      ? product.product_id : null,
    range: product?.range || null,
    face: rangeFace,
  });

  // The cart and the purchase endpoints must carry the server's figure, not the
  // one used to ask the question with.
  useEffect(() => {
    if (!rangeQuote) return;
    setSelectedDenomination({
      id: `${product.product_id}_custom`,
      value: Number(rangeQuote.payable_amount),
      face: Number(rangeQuote.face_value),
      savings: Number(rangeQuote.discount_amount) || 0,
      discountType: rangeQuote.discount_type,
      discountValue: Number(rangeQuote.discount_value),
    });
    setProduct((prev) => ({ ...prev, price: Number(rangeQuote.payable_amount) }));
  }, [rangeQuote, product?.product_id]);

  /*
    Nothing to buy yet: a fixed card with values on offer and none picked, or a
    range card whose typed value has not come back priced. Both leave the
    buttons dead rather than live-then-complaining.
  */
  const needsDenomination = !!product?.is_gift_card && (
    (product.denominations?.length > 0 && !selectedDenomination)
    || (product.denomination_type === 'range' && !rangeQuote)
  );

  const getImageUrl = (url) => {
    if (!url) return PLACEHOLDER_IMAGE;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-8 py-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="aspect-square bg-surface rounded-3xl animate-pulse" />
            <div className="space-y-4">
              <div className="h-8 bg-surface rounded-xl animate-pulse w-3/4" />
              <div className="h-6 bg-surface rounded-xl animate-pulse w-1/2" />
              <div className="h-12 bg-surface rounded-xl animate-pulse w-1/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Gift cards carry a synthetic stock of 999; the real ceiling is the
  // provider's per-denomination cap.
  const maxQuantity = product?.is_gift_card
    ? GIFT_CARD_MAX_QUANTITY
    : (Number(product?.stock) || 1);

  if (!product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-3xl text-secondary mb-4">Product Not Found</h1>
          <Button onClick={() => navigate('/')} className="rounded-full">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  const discount = product.original_price
    ? Math.round((1 - product.price / product.original_price) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-white" data-testid="product-page">
      <SEO 
        title={product.meta_title || product.name} 
        description={product.meta_description || product.short_description?.replace(/<[^>]*>/g, '') || product.description?.replace(/<[^>]*>/g, '').substring(0, 160)} 
        keywords={product.meta_keywords}
        image={getImageUrl(product.images?.[0])}
        url={`https://www.nso-india.com/product/${product.product_id}`}
      />

      {/*
        Product structured data. Without it Google shows a plain blue link; with it the
        result can carry price, availability and a star rating, which is the difference
        between being scrolled past and being clicked.
        React 19 hoists this into <head> automatically.
      */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: product.name,
          description: (product.short_description || product.description || '')
            .replace(/<[^>]*>/g, '')
            .slice(0, 500),
          image: (product.images || []).map(getImageUrl).filter(Boolean),
          sku: product.product_id,
          ...(product.brand ? { brand: { '@type': 'Brand', name: product.brand.trim() } } : {}),
          offers: {
            '@type': 'Offer',
            url: `https://www.nso-india.com/product/${product.product_id}`,
            priceCurrency: 'INR',
            price: Number(product.price) || 0,
            // Gift cards are always fulfillable; physical stock decides the rest.
            availability: (product.is_gift_card || product.stock > 0)
              ? 'https://schema.org/InStock'
              : 'https://schema.org/OutOfStock',
            seller: { '@type': 'Organization', name: 'NSO India' }
          },
          // Only emit a rating when real reviews exist — inventing one is exactly what
          // gets structured data penalised.
          ...(reviews.length > 0
            ? {
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: (reviews.reduce((acc, r) => acc + Number(r.rating || 0), 0) / reviews.length).toFixed(1),
                reviewCount: reviews.length
              }
            }
            : {})
        })}
      </script>
      <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
          {/* Image Gallery.
              Sticky on desktop: the buy column is two to three times taller than
              the media, so once you scrolled past the image the whole left half
              of the page was blank white for the rest of the scroll. */}
          <div
            className="space-y-4 min-w-0 overflow-hidden lg:sticky lg:top-24 lg:self-start"
            data-testid="product-gallery"
          >
            {/* Main Image */}
            <div className={`relative aspect-square rounded-3xl flex items-center justify-center overflow-hidden ${
              product.is_gift_card
                ? 'border border-slate-200/70 bg-gradient-to-br from-slate-50 via-white to-slate-100 p-6 sm:p-10'
                : 'bg-white border border-gray-100'}`}>
              {product.is_gift_card ? (
                /*
                  A gift card's images are fallbacks for one another, not a gallery.
                  RewardImage walks them until one loads and ends on a branded tile,
                  so a card with no usable artwork still looks intentional.

                  The logo sits on a voucher-shaped plate rather than free-floating:
                  brand marks are small and wide, and centring one in a bare square
                  left it looking like a failed image load.
                */
                <div className="relative w-full max-w-[440px]">
                  <GiftCardTile
                    reward={{ name: product.name, display_image_candidates: product.images || [] }}
                    size="lg"
                    className="shadow-[0_20px_50px_-24px_rgba(15,23,42,0.4)]"
                  />
                </div>
              ) : (
                <img
                  src={getImageUrl(product.images?.[selectedImage])}
                  alt={product.name}
                  onError={(e) => { if (e.currentTarget.src !== PLACEHOLDER_IMAGE) e.currentTarget.src = PLACEHOLDER_IMAGE; }}
                  className="max-w-full max-h-full object-contain"
                />
              )}

              {!product.is_gift_card && product.images?.length > 1 && (
                <>
                  <button
                    onClick={() => setSelectedImage((prev) => (prev - 1 + product.images.length) % product.images.length)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/80 backdrop-blur rounded-full hover:bg-white transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setSelectedImage((prev) => (prev + 1) % product.images.length)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/80 backdrop-blur rounded-full hover:bg-white transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnail Images */}
            {!product.is_gift_card && product.images?.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2 w-full max-w-full min-w-0">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0 border-2 transition-all ${selectedImage === idx ? 'border-primary' : 'border-transparent'
                      }`}
                  >
                    <img src={getImageUrl(img)} alt="" onError={(e) => { if (e.currentTarget.src !== PLACEHOLDER_IMAGE) e.currentTarget.src = PLACEHOLDER_IMAGE; }} className="w-full h-full object-contain p-2" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6 min-w-0" data-testid="product-info">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground font-body">
              <button onClick={() => navigate(-1)} className="hover:text-primary">
                <ChevronLeft className="w-4 h-4 inline" /> Back
              </button>
            </div>

            {/* Brand */}
            <p className="text-sm uppercase tracking-widest text-primary font-body font-semibold">
              {product.brand}
            </p>

            {/* Title */}
            <h1 className="font-heading text-3xl md:text-4xl tracking-tight text-secondary">
              {product.name}
            </h1>

            {/* Rating.
                Hidden on gift cards: the reviews endpoint is never called for a
                reward (the gift-card branch of the fetch returns before it), so
                this could only ever render five empty stars and an invitation to
                review that nothing in the app can act on. */}
            {!product.is_gift_card && (
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${i < Math.round(reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length || 0)
                        ? 'fill-warning text-warning'
                        : 'text-gray-200'
                        }`}
                    />
                  ))}
                </div>
                {reviews.length > 0 ? (
                  <>
                    <span className="font-body font-semibold text-secondary">
                      {(reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1)}
                    </span>
                    <span className="text-muted-foreground font-body">
                      ({reviews.length} reviews)
                    </span>
                  </>
                ) : (
                  <span className="text-muted-foreground font-body text-sm italic">
                    No reviews yet. Be the first to review!
                  </span>
                )}
              </div>
            )}

            {/* Price */}
            <div className="flex flex-col gap-3">
              <div className="flex items-end gap-4">
                {/*
                  For a gift card the headline must track the chip the customer
                  actually picked. It used to read `product.price`, which is set
                  once from `display_price` (the cheapest option) and never
                  moves — so selecting the ₹2,220 denomination still advertised
                  ₹503.99 directly above a Buy button that would charge ₹2,220.
                */}
                {/*
                  The header states the range, not the selection. What was
                  chosen belongs in the pricing summary below the grid — one
                  place, so the two can never disagree, and the customer is not
                  reading the same numbers in three sizes.
                */}
                {product.is_gift_card && cardStartingValue != null && (
                  <span className="font-body text-lg text-muted-foreground">From</span>
                )}
                <span className="font-body font-bold text-4xl text-secondary" data-testid="product-price">
                  {product.is_gift_card
                    ? formatMoney(cardStartingValue * quantity)
                    : formatPrice(product.discount_type === 'b1g1' && parseFloat(product.price) === 0
                      ? product.original_price * quantity
                      : product.price * quantity)}
                </span>
                {product.is_gift_card && giftCardLabel && (
                  <span className="rounded-full bg-success/10 px-3 py-1 font-body text-sm font-semibold text-success">
                    {giftCardLabel}
                  </span>
                )}
                {product.original_price > product.price && product.discount_type !== 'b1g1' && (
                  <>
                    <span className="text-xl text-muted-foreground line-through font-body">
                      {formatPrice(product.original_price * quantity)}
                    </span>
                    <span className="bg-destructive/10 text-destructive px-3 py-1 rounded-full text-sm font-medium font-body">
                      {discount}% OFF
                    </span>
                  </>
                )}
              </div>

              {/* B1G1 Special Offer Badge */}
              {product.discount_type === 'b1g1' && (
                <div className="flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-blue-600 text-white px-4 py-2 rounded-2xl w-fit shadow-md shadow-indigo-100 animate-in slide-in-from-left-2 duration-500">
                  <Gift className="w-4 h-4 fill-white/20" />
                  <span className="text-xs font-bold uppercase tracking-wider">Buy 1 Get 1 FREE</span>
                </div>
              )}
            </div>

            {/*
              Range (variable-amount) gift cards.

              The input holds the gift card's **face value** — what the card is
              worth and what the recipient sees. It used to be prefilled with the
              payable amount, so the header read "From ₹10" above an input
              showing ₹9.80 and the customer had to work out that these were the
              same card.

              Free entry between the catalogue's own bounds. There used to be a
              row of "suggested" chips here — ₹98, ₹490, ₹980 — generated by
              multiplying the floor. None of those amounts came from the API.
            */}
            {product.is_gift_card && product.denomination_type === 'range' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label
                    className="font-body text-sm font-medium text-secondary"
                    htmlFor="gift-card-face-value"
                  >
                    Enter gift card value ({formatPrice(product.min_amount)} – {formatPrice(product.max_amount)})
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="font-body text-lg font-semibold text-secondary">₹</span>
                    <input
                      id="gift-card-face-value"
                      type="number"
                      min={product.min_amount}
                      max={product.max_amount}
                      value={rangeFace}
                      onChange={(e) => setRangeFace(e.target.value === '' ? '' : Number(e.target.value))}
                      className="w-44 rounded-xl border-2 border-gray-100 px-4 py-2 font-body text-sm outline-none focus:border-primary"
                      data-testid="gift-card-amount-input"
                    />
                  </div>
                  {rangeFace !== '' && (Number(rangeFace) < product.min_amount || Number(rangeFace) > product.max_amount) && (
                    <p className="font-body text-xs text-destructive">
                      Value must be between {formatPrice(product.min_amount)} and {formatPrice(product.max_amount)}.
                    </p>
                  )}
                </div>

                {/* Priced by the server. Every figure below is the quote's own —
                    face_value, discount_amount, payable_amount — so the total
                    here is the total that gets charged. */}
                {rangeQuote ? (
                  <div
                    className="rounded-2xl border border-slate-200 bg-surface p-5"
                    data-testid="range-summary"
                  >
                    <dl className="space-y-3 font-body text-sm">
                      <div className="flex items-baseline justify-between">
                        <dt className="text-muted-foreground">Gift card value</dt>
                        <dd className="font-semibold text-secondary">
                          {formatMoney(rangeQuote.face_value * quantity)}
                        </dd>
                      </div>
                      {rangeQuote.markup_direction === 'surcharge' ? (
                        <>
                          <div className="flex items-baseline justify-between">
                            <dt className="text-muted-foreground">Surcharge</dt>
                            <dd className="font-semibold text-amber-600">
                              {rangeQuote.surcharge_value > 0
                                ? (rangeQuote.surcharge_type === 'percentage'
                                  ? `+${Number(Number(rangeQuote.surcharge_value).toFixed(2))}%`
                                  : `+${formatMoney(rangeQuote.surcharge_value)}`)
                                : `+${formatMoney(rangeQuote.surcharge_amount ?? 0)}`}
                            </dd>
                          </div>
                          <div className="flex items-baseline justify-between">
                            <dt className="text-muted-foreground">Total Surcharge</dt>
                            <dd className="font-semibold text-amber-600">
                              +{formatMoney((rangeQuote.surcharge_amount ?? 0) * quantity)}
                            </dd>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-baseline justify-between">
                            <dt className="text-muted-foreground">Discount</dt>
                            <dd className={`font-semibold ${rangeQuote.discount_amount > 0 ? 'text-success' : 'text-secondary'}`}>
                              {rangeQuote.discount_value > 0
                                ? (rangeQuote.discount_type === 'percentage'
                                  ? `${Number(Number(rangeQuote.discount_value).toFixed(2))}%`
                                  : formatMoney(rangeQuote.discount_value))
                                : formatMoney(rangeQuote.discount_amount ?? 0)}
                            </dd>
                          </div>
                          <div className="flex items-baseline justify-between">
                            <dt className="text-muted-foreground">You save</dt>
                            <dd className={`font-semibold ${rangeQuote.discount_amount > 0 ? 'text-success' : 'text-secondary'}`}>
                              {formatMoney((rangeQuote.discount_amount ?? 0) * quantity)}
                            </dd>
                          </div>
                        </>
                      )}
                      <div className="flex items-baseline justify-between border-t border-slate-200 pt-3">
                        <dt className="font-semibold text-secondary">You pay</dt>
                        <dd
                          className="font-body text-2xl font-bold text-secondary"
                          data-testid="final-payable"
                        >
                          {formatMoney(rangeQuote.payable_amount * quantity)}
                        </dd>
                      </div>
                    </dl>
                    {quantity > 1 && (
                      <p className="mt-2 text-right font-body text-[11px] text-muted-foreground">
                        {quantity} × {formatPrice(rangeQuote.payable_amount)}
                      </p>
                    )}
                  </div>
                ) : rangePricing ? (
                  /* Same rows, dashes instead of figures. A panel that collapses
                     and reappears on every keystroke is harder to read than one
                     that holds its shape, and "—" is honest about not knowing
                     yet in a way that ₹0 is not. */
                  <div className="rounded-2xl border border-slate-200 bg-surface p-5" aria-busy="true">
                    <dl className="space-y-3 font-body text-sm">
                      {['Gift card value', 'Discount', 'You save'].map((label) => (
                        <div key={label} className="flex items-baseline justify-between">
                          <dt className="text-muted-foreground">{label}</dt>
                          <dd className="font-semibold text-muted-foreground">—</dd>
                        </div>
                      ))}
                      <div className="flex items-baseline justify-between border-t border-slate-200 pt-3">
                        <dt className="font-semibold text-secondary">You pay</dt>
                        <dd className="font-body text-2xl font-bold text-muted-foreground">—</dd>
                      </div>
                    </dl>
                  </div>
                ) : (
                  <p className="font-body text-[13px] text-muted-foreground">
                    Enter a value to see what you pay.
                  </p>
                )}
              </div>
            )}

            {/*
              Denomination selector.

              The cards carry one thing each: the value. Every one of them used
              to repeat the badge, "you pay" and "you save" — nine cards saying
              "2% OFF" nine times, which is noise, not information. The rate is
              in the header once, and the arithmetic is in the summary below,
              once. A card here answers only "which value", which is the single
              decision being made.
            */}
            {product.is_gift_card && product.denominations?.length > 0 && (
              <div className="space-y-4">
                <span className="font-body text-sm font-medium text-secondary">
                  Select gift card value
                </span>

                <div
                  className="grid grid-cols-3 gap-2.5 sm:grid-cols-4"
                  role="radiogroup"
                  aria-label="Gift card value"
                >
                  {product.denominations.map((denom) => {
                    const active = selectedDenomination?.id === denom.id;
                    return (
                      <button
                        key={denom.id}
                        role="radio"
                        aria-checked={active}
                        onClick={() => {
                          setSelectedDenomination(denom);
                          setProduct((prev) => ({ ...prev, price: denom.value }));
                        }}
                        className={`relative rounded-2xl border-2 px-3 py-4 text-center transition-all ${active
                          ? 'border-primary bg-primary/[0.06]'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50/70'}`}
                        data-testid={`denomination-${denom.value}`}
                      >
                        {active && (
                          <span className="absolute right-1.5 top-1.5 grid h-5 w-5 place-items-center rounded-full bg-primary text-white">
                            <Check className="h-3 w-3" strokeWidth={3} />
                          </span>
                        )}
                        <p className="font-body text-[17px] font-bold leading-none text-secondary">
                          {formatMoney(denom.face ?? denom.value)}
                        </p>
                        <p className="mt-1 font-body text-[11px] text-muted-foreground">Gift card</p>
                      </button>
                    );
                  })}
                </div>

                {/*
                  The one place any figure is spelled out. Every line reads a
                  published field of the chosen row — face_value, discount_value,
                  discount_amount, payable_amount — so nothing here is computed
                  and nothing is left for the customer to work out.
                */}
                {selectedDenomination ? (
                  <div
                    className="rounded-2xl border border-slate-200 bg-surface p-5"
                    data-testid="denomination-summary"
                  >
                    {/*
                      Rows are always present, so switching from a discounted
                      card to one without does not change the shape of the panel.
                      A real zero prints as ₹0; a figure that has not arrived
                      prints as "—", because ₹0 is the one value a gift card
                      cannot have and stating it would be a wrong price.
                    */}
                    <dl className="space-y-3 font-body text-sm">
                      <div className="flex items-baseline justify-between">
                        <dt className="text-muted-foreground">Gift card value</dt>
                        <dd className="font-semibold text-secondary">
                          {formatMoney((selectedDenomination.face ?? selectedDenomination.value) * quantity)}
                        </dd>
                      </div>

                      <div className="flex items-baseline justify-between">
                        <dt className="text-muted-foreground">Discount</dt>
                        <dd className={`font-semibold ${selectedDenomination.savings > 0 ? 'text-success' : 'text-secondary'}`}>
                          {formatDiscountRate(selectedDenomination) || formatMoney(selectedDenomination.savings ?? 0)}
                        </dd>
                      </div>

                      <div className="flex items-baseline justify-between">
                        <dt className="text-muted-foreground">You save</dt>
                        <dd className={`font-semibold ${selectedDenomination.savings > 0 ? 'text-success' : 'text-secondary'}`}>
                          {formatMoney((selectedDenomination.savings ?? 0) * quantity)}
                        </dd>
                      </div>

                      <div className="flex items-baseline justify-between border-t border-slate-200 pt-3">
                        <dt className="font-semibold text-secondary">You pay</dt>
                        <dd
                          className="font-body text-2xl font-bold text-secondary"
                          data-testid="final-payable"
                        >
                          {formatMoney(selectedDenomination.value * quantity)}
                        </dd>
                      </div>
                    </dl>

                    {quantity > 1 && (
                      <p className="mt-2 text-right font-body text-[11px] text-muted-foreground">
                        {quantity} × {formatPrice(selectedDenomination.value)}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="font-body text-[13px] text-muted-foreground">
                    Pick a value to see what you pay.
                  </p>
                )}
              </div>
            )}

            {/* Stock Status */}
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${product.stock > 0 ? 'bg-success' : 'bg-destructive'}`} />
              <span className="text-sm font-body">
                {product.is_gift_card ? 'In Stock (Instant Delivery)' : (product.stock > 0 ? `In Stock (${product.stock} available)` : 'Out of Stock')}
              </span>
            </div>

            {/* Short Description / Highlights */}
            {product.short_description && (
              <div
                className="prose-custom text-sm bg-surface/40 p-1 rounded-3xl border border-gray-100/50"
                // Was injected with only a whitespace tidy-up on it — no sanitiser
                // at all, while the long description two blocks down had one.
                dangerouslySetInnerHTML={{
                  __html: sanitizeCatalogueHtml(
                    (product.short_description || '').replace(/(&nbsp;)+/g, ' ').replace(/\s\s+/g, ' ')
                  )
                }}
              />
            )}

            {/* Quantity & Actions */}
            <div className="space-y-4 pt-4 border-t border-gray-100">
              {/* Quantity */}
              <div className="flex items-center gap-4">
                <span className="text-sm font-body font-medium text-secondary">Quantity:</span>
                <div className="flex items-center border border-gray-200 rounded-full">
                  <button
                    onClick={() => {
                      if (quantity > 1) {
                        setQuantity(quantity - 1);
                      } else {
                        toast.info('Minimum 1 unit required');
                      }
                    }}
                    className="p-2 hover:bg-surface transition-colors rounded-l-full"
                    data-testid="decrease-qty"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <input
                    type="number"
                    min="1"
                    max={maxQuantity}
                    value={quantity}
                    onChange={(e) => {
                      // Allow empty string so users can delete and type new numbers
                      if (e.target.value === '') {
                        setQuantity('');
                        return;
                      }
                      
                      const val = parseInt(e.target.value, 10);
                      if (isNaN(val)) return;

                      if (val > maxQuantity) {
                        if (product?.is_gift_card) {
                          toast.error(`You can buy up to ${GIFT_CARD_MAX_QUANTITY} of the same gift card in one order.`);
                        } else {
                          toast.error(`Only ${product?.stock} units available in stock`);
                        }
                        setQuantity(maxQuantity);
                      } else {
                        setQuantity(val);
                      }
                    }}
                    onBlur={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (isNaN(val) || val < 1) {
                        setQuantity(1);
                      }
                    }}
                    className="w-12 text-center font-body font-medium bg-transparent border-none p-0 focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    data-testid="quantity-value"
                  />
                  <button
                    onClick={() => {
                      if (quantity < maxQuantity) {
                        setQuantity(quantity + 1);
                      } else if (product.is_gift_card) {
                        toast.error(`You can buy up to ${GIFT_CARD_MAX_QUANTITY} of the same gift card in one order.`);
                      } else {
                        toast.error(`Only ${product.stock} units available in stock`);
                      }
                    }}
                    disabled={quantity >= maxQuantity}
                    className="p-2 hover:bg-surface transition-colors rounded-r-full disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    data-testid="increase-qty"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Action Buttons. Both are dead until a gift card has a value —
                  there is nothing to add or charge for until then, and a button
                  that looks live and then complains is worse than one that
                  waits. */}
              <div className="flex flex-col sm:flex-row gap-4 w-full">
                {/* Gift cards get this too now: a customer buying vouchers for a
                    whole team should not have to run one payment per card. */}
                <Button
                  onClick={product.is_gift_card ? handleAddGiftCardToCart : handleAddToCart}
                  disabled={product.stock === 0 || needsDenomination}
                  className="w-full sm:flex-1 bg-brand-orange hover:bg-brand-orange-dark text-secondary rounded-full py-6 font-body font-semibold btn-press disabled:opacity-50"
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Add to Cart
                </Button>
                <Button
                  onClick={handleBuyNow}
                  disabled={product.stock === 0 || isProcessingOrder || needsDenomination}
                  className="w-full sm:flex-1 bg-primary hover:bg-primary/90 text-white rounded-full py-6 font-body btn-press disabled:opacity-50"
                  data-testid="buy-now-btn"
                >
                  {isProcessingOrder ? 'Processing...' : 'Buy Now'}
                </Button>
                {!product.is_gift_card && (
                  <Button
                    onClick={handleWishlist}
                    variant="outline"
                    className={`p-6 rounded-full transition-all duration-300 ${inWishlist ? 'bg-red-50 border-red-200' : 'hover:border-red-200 hover:bg-red-50/50'}`}
                    data-testid="wishlist-btn"
                  >
                    <Heart className={`w-5 h-5 transition-all duration-300 ${inWishlist ? 'fill-red-500 text-red-500 scale-110' : 'text-muted-foreground'}`} />
                  </Button>
                )}
              </div>
            </div>

            {/* Delivery serviceability — before checkout, not after */}
            {!product.is_gift_card && <PincodeCheck />}

            {/* Everything the catalogue knows about redeeming this voucher. */}
            {product.is_gift_card && (
              <div className="pt-6 border-t border-gray-100">
                <GiftCardInfo product={product} />
              </div>
            )}

            {/* Features Icons — a voucher has no shipping, warranty or return
                window, so promising all three on a gift card was simply false. */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-gray-100">
              {(product.is_gift_card
                ? [
                  {
                    icon: Zap,
                    label: 'Instant Delivery',
                    detail: 'Your voucher code is issued as soon as payment clears — nothing is shipped, and there is nothing to wait for.'
                  },
                  {
                    icon: ShieldCheck,
                    label: 'Secure Payment',
                    detail: 'Paid through our payment gateway or your NSO wallet. Cash on delivery is not available on gift cards.'
                  },
                  {
                    icon: Gift,
                    label: 'Brand Voucher',
                    detail: 'Issued directly by the brand and redeemable on their own channels. See "How to redeem" above for the exact steps.'
                  }
                ]
                : [
                  {
                    icon: Truck,
                    label: 'Free Delivery',
                    detail: 'Enjoy free standard delivery on all orders above ₹499. No coupon code required!'
                  },
                  {
                    icon: Shield,
                    label: 'Warranty',
                    detail: 'This product comes with a 1-year manufacturer warranty covering manufacturing defects.'
                  },
                  {
                    icon: RefreshCw,
                    label: 'Easy Returns',
                    detail: 'Not satisfied? Return your product within 7 days of delivery for a full refund or exchange.'
                  }
                ]
              ).map((feature, idx) => (
                <button 
                  key={idx} 
                  onClick={() => setSelectedFeature(feature)}
                  className="text-center p-4 bg-surface hover:bg-surface/80 rounded-2xl transition-all group active:scale-95 flex flex-col items-center"
                >
                  <feature.icon className="w-6 h-6 mx-auto mb-2 text-primary group-hover:scale-110 transition-transform" />
                  <span className="text-xs font-body text-muted-foreground font-medium">{feature.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Product Details Tabs */}
        <div className={`space-y-8 ${product.is_gift_card ? 'mt-10' : 'mt-16'}`}>
          {/* Description */}
          {product.is_gift_card ? (
            /*
              A voucher description is one short paragraph from the catalogue.
              Running it through the "Full Product Experience" banner and a
              collapsible accordion gave three lines of copy two layers of chrome
              and a screen of empty space, so it is rendered flat here — and the
              measure is capped, because a 1,400px line of body text is unreadable
              however much room the grid has.
            */
            product.description ? (
              <section className="rounded-3xl border border-slate-200/70 bg-slate-50/60 p-6 sm:p-8">
                <h2 className="font-heading text-xl text-secondary mb-3">
                  About {product.brand_name || product.name}
                </h2>
                <div
                  // last-child margin killed: prose-custom puts 1.25em under every
                  // paragraph, which reads as dead padding at the bottom of a card.
                  className="prose-custom max-w-3xl font-body text-sm leading-relaxed text-muted-foreground [&>*:last-child]:mb-0"
                  dangerouslySetInnerHTML={{ __html: sanitizeCatalogueHtml(product.description) }}
                />
              </section>
            ) : null
          ) : (
            <div className="bg-white rounded-3xl p-2 sm:p-4 lg:p-6 overflow-hidden">
              <h2 className="font-heading text-2xl md:text-3xl text-secondary mb-8 pb-4 border-b border-gray-200/50">Full Product Experience</h2>
              <CollapsibleDescription html={product.description} />
            </div>
          )}

          {/* Specifications */}
          {product.specifications && Object.keys(product.specifications).length > 0 && (
            <div className="bg-surface rounded-3xl p-5 sm:p-8">
              <h2 className="font-heading text-2xl text-secondary mb-4">Specifications</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {Object.entries(product.specifications).map(([key, value]) => (
                  <div key={key} className="flex justify-between py-3 border-b border-gray-200">
                    <span className="font-body text-muted-foreground">{key}</span>
                    <span className="font-body font-medium text-secondary">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reviews — products only. A reward has no reviews and no way to leave
              one, so on a gift card this was half a screen of empty state. */}
          {!product.is_gift_card && (
          <div className="bg-surface rounded-3xl p-5 sm:p-8" id="reviews">
            <h2 className="font-heading text-2xl text-secondary mb-6">
              Customer Reviews ({reviews.length})
            </h2>
            {reviews.length > 0 ? (
              <div className="space-y-6">
                {reviews.map((review) => (
                  <div key={review.review_id || review.id} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <span className="font-body font-bold text-secondary text-sm block">{review.user_name || 'Anonymous'}</span>
                        <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-black">Verified Purchase</span>
                      </div>
                      <div className="flex gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-3.5 h-3.5 ${i < review.rating ? 'fill-warning text-warning' : 'text-gray-200'
                              }`}
                          />
                        ))}
                      </div>
                    </div>
                    {review.comment && (
                      <p className="text-muted-foreground font-body text-sm leading-relaxed">{review.comment}</p>
                    )}
                    <div className="mt-4 pt-4 border-t border-gray-50 flex items-center gap-4 opacity-50">
                      <span className="text-[10px] font-body">Was this helpful?</span>
                      <button className="text-[10px] font-bold hover:text-primary transition-colors">HELPFUL</button>
                      <button className="text-[10px] font-bold hover:text-primary transition-colors">REPORT</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-gray-200">
                <MessageSquare className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                <p className="text-muted-foreground font-body">Be the first to review this product!</p>
              </div>
            )}
          </div>
          )}
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-16">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-3xl text-secondary">Related Products</h2>
              <Link
                to={`/category/${product?.category_id?.replace('cat_', '')}`}
                className="flex items-center gap-2 text-primary font-body font-medium hover:gap-3 transition-all"
              >
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {relatedProducts.map((product) => (
                <ProductCard key={product.product_id} product={product} />
              ))}
            </div>
          </div>
        )}
      </div>
      <GiftCardPaymentModal
        open={giftPayModal}
        onClose={() => setGiftPayModal(false)}
        onConfirm={submitGiftCardPurchase}
        productName={product.name}
        amount={selectedDenomination?.value || product.price}
        quantity={quantity}
        walletBalance={walletBalance}
        processing={isProcessingOrder}
        formatPrice={formatPrice}
      />

      <GiftCardSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        voucher={voucherDetails || { code: 'N/A', pin: 'N/A' }}
      />

      <FeatureDetailModal 
        isOpen={!!selectedFeature}
        onClose={() => setSelectedFeature(null)}
        feature={selectedFeature}
      />

      <GiftCardGatewayModal
        open={paymentModal.open}
        url={paymentModal.url}
        orderId={paymentModal.orderId}
        amount={paymentModal.amount}
        status={paymentModal.status}
        checking={checkingStatus}
        refund={paymentModal.refund}
        onCheckStatus={manualCheckStatus}
        onRetry={() => {
          setPaymentModal(prev => ({ ...prev, status: 'pending' }));
          startPollingPaymentStatus({
            orderId: paymentModal.orderId,
            referenceId: paymentModal.referenceId,
          });
        }}
        onClose={() => {
          stopPolling();
          const wasPending = paymentModal.status === 'pending';
          setPaymentModal({ open: false, url: '', orderId: '', status: 'pending' });
          setIsProcessingOrder(false);
          if (wasPending) toast('Payment window closed. Your transaction was cancelled.', { icon: 'ℹ️' });
        }}
      />
    </div>
  );
}
